import type { Vehicle } from '@cruz-com/lookup';
import _ from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { vehicleApi } from '@/api';
import { Checkbox, Text } from '@/components';
import { Section } from './vehicleFilterSection';
import { VehicleMakeModelListBox } from './vehicleMakeModelListBox';
type mmQuery = {
  make?: string;
  model?: string | null;
  trim?: string | null;
};
type Props = {
  type?: string;
  className?: string;
  name: string;
  title: string;
  otherQuery: Vehicle.Query;
  mm: string;
  makeOptions: Vehicle.MakeModelOption;
  onChange: (val: string | null) => void;
} & Omit<React.ComponentPropsWithoutRef<'select'>, 'onChange'>;
const ignoreFilters = ['mm1', 'mm2', 'mm3', 'make', 'model', 'trim'];
const mmStyle = 'text-sm pb-2 w-[60px] font-medium';

// For VehicleFilter1Min
export function VehicleMakeModel({
  className,
  name,
  title,
  type,
  otherQuery,
  mm,
  makeOptions,
  onChange
}: Props) {
  function getQueryFromMmt() {
    if (!mm) {
      return {
        make: ''
      };
    }
    const y = _.split(mm, ',');
    return {
      make: y[0],
      model: y[1]
    };
  }
  const sectionRef = useRef<null | HTMLDivElement>(null);
  const [query, setQuery] = useState<mmQuery>(getQueryFromMmt());
  const [isMakeChanged, setIsMakeChanged] = useState(false); // used for scroll into view
  const [modelOption, setModelOption] = useState<string[] | null>(null);
  const [modelKeyword, setModelKeyword] = useState('');

  // mm changed
  useEffect(() => {
    setQuery(getQueryFromMmt());
  }, [mm]);

  // update parent
  useEffect(() => {
    if (query?.make) {
      onChange(_.compact([query.make, query?.model]).join(','));
    } else {
      onChange(null);
    }
  }, [query]);

  // make changed
  function makeChanged(val: string) {
    setQuery({
      make: val,
      model: null
    });
  }
  useEffect(() => {
    setModelOption(null);
    setIsMakeChanged(true);
    if (query?.make) {
      getModelOption();
    }
  }, [query?.make]);

  // models
  async function getModelOption() {
    const d = await vehicleApi.getModelOption({
      ..._.omit(otherQuery, ignoreFilters),
      make: query.make
    }, type);
    const res = _.concat(d?.popular, d?.other) as string[];
    setModelOption(res);
  }
  useEffect(() => {
    if (!_.isEmpty(modelOption) && isMakeChanged) {
      sectionRef?.current?.scrollIntoView({
        behavior: 'smooth'
      });
      setIsMakeChanged(false);
    }
  }, [modelOption]);
  function modelSelected(model: string, val_0: boolean) {
    const x: string[] = query?.model ? _.split(query.model as string, '|') : [];
    const y_0 = val_0 ? _.concat(x, [model]) : _.without(x, model);
    setQuery({
      ...query,
      model: _.join(y_0, '|')
    });
  }
  const filteredModels = useCallback(() => {
    if (_.isEmpty(modelKeyword)) {
      return modelOption;
    }
    return _.filter(modelOption, (x_0: string) => x_0.toLowerCase().includes(modelKeyword.toLowerCase()));
  }, [modelOption, modelKeyword]);

  // models
  const Models = () => {
    if (_.isEmpty(modelOption)) {
      return null;
    }
    return <fieldset className='flex w-full items-start'>
        <legend className={mmStyle}>Models:</legend>
        <div className='flex-1'>
          <div className='mb-3 w-full md:w-[200px]'>
            <Text placeholder='Search Model' compact={true} name='mf' type='text' value={modelKeyword} onChange={e => setModelKeyword(e.target.value || '')} />
          </div>
          <ul className='mt-1 grid grid-cols-1 gap-x-4 md:grid-cols-2'>
            {_.map(filteredModels(), (name_0, idx) => {
            const x_1 = _.split(query?.model, '|');
            return <li key={idx}>
                  <Checkbox name={name_0} label={name_0} checked={x_1.includes(name_0)} onChange={val_1 => modelSelected(name_0, val_1)} />
                </li>;
          })}
          </ul>
        </div>
      </fieldset>;
  };
  return <div ref={sectionRef} className='w-full'>
      <Section name={name} title={title} hasValue={!_.isEmpty(query?.make)} className={className}>
        <div className='flex w-full flex-col gap-y-3' ref={sectionRef}>
          <fieldset className='flex items-start'>
            <legend className={mmStyle}>Make:</legend>
            <div className='w-[200px]'>
              <VehicleMakeModelListBox value={query?.make || 'Any'} option={makeOptions} onChange={val_2 => makeChanged(_.toString(val_2))} />
            </div>
          </fieldset>
          {Models()}
        </div>
      </Section>
    </div>;
}